import React, { useState } from 'react';

/**
 * Agreement Generator Component
 * Multi-step wizard for creating rental/lease agreements
 */

export default function AgreementGenerator({ 
  buildings, 
  units,
  user 
}) {
  // Note: We allow number input arrows for Agreement Duration in Lease agreements
  // All other fields use type="text" with inputMode="numeric"

  // Main state
  const [view, setView] = useState('list'); // 'list' | 'create'
  const [agreementType, setAgreementType] = useState(null); // 'rental' | 'lease'
  const [currentStep, setCurrentStep] = useState(1); // 1-7
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  // Form data
  const [formData, setFormData] = useState({
    // Basic Details (Step 1)
    tenant_name: '',
    tenant_id_type: 'aadhaar', // 'aadhaar' | 'pan' | 'passport' | 'driving_license' | 'voter_id'
    tenant_id_number: '',
    tenant_phone: '',
    tenant_address: '', // Changed from tenant_email
    move_in_date: '',
    agreement_duration: 11, // months (select dropdown - keep as number)
    monthly_rent: '',
    advance_deposit: '',
    lease_amount: '', // NEW: One-time lease amount (for lease agreements only)
    payment_date: 5, // day of month (select dropdown - keep as number)
    escalation_percent: '5', // Changed to string
    notice_period: 2, // months (select dropdown - keep as number)
    
    // Legal Clauses (Step 2) - all boolean
    clause_subletting: true,
    clause_maintenance: true,
    clause_structural: true,
    clause_illegal: true,
    clause_late_penalty: false,
    clause_late_penalty_amount: '100', // Changed to string
    clause_inspection: true,
    clause_pets: false,
    clause_pets_allowed: false,
    clause_parking: false,
    clause_parking_slot: '',
    clause_painting: true,
    clause_society_rules: true,
    clause_insurance: false,
    clause_arbitration: true,
    
    // Property Schedule (Step 3)
    furnishing: 'unfurnished', // 'fully' | 'semi' | 'unfurnished'
    bedrooms: '1', // Changed to string
    bathrooms: '1', // Changed to string
    has_hall: true,
    has_kitchen: true,
    ac_count: '0', // Changed to string
    geyser_count: '0', // Changed to string
    fridge_count: '0', // Changed to string
    washing_machine_count: '0', // Changed to string
    microwave_count: '0', // Changed to string
    tv_count: '0', // Changed to string
    sofa_count: '0', // Changed to string
    bed_count: '0', // Changed to string
    dining_table_count: '0', // Changed to string
    wardrobe_count: '0', // Changed to string
    fan_count: '2', // Changed to string
    light_count: '4', // Changed to string
    additional_notes: '',
    
    // Payment (Step 4)
    stamp_value: 100, // 100 | 500 (select - keep as number)
  });
  
  // Calculated values
  const getAgreementEndDate = () => {
    if (!formData.move_in_date) return '';
    const start = new Date(formData.move_in_date);
    start.setMonth(start.getMonth() + formData.agreement_duration);
    return start.toISOString().split('T')[0];
  };
  
  const getTotalCost = () => {
    const stamp = formData.stamp_value;
    const printing = 90; // Changed from 50/100
    const courier = 60; // Changed from 50
    const service = 49; // Changed from 49/99
    return stamp + printing + courier + service;
  };

  // Helper function to prepare agreement data with proper type conversion
  const prepareAgreementData = () => {
    return {
      agreement_type: agreementType || 'rental',
      unit_id: selectedUnit.id,
      
      // Tenant details (strings - no conversion needed)
      tenant_name: formData.tenant_name,
      tenant_id_type: formData.tenant_id_type,
      tenant_id_number: formData.tenant_id_number,
      tenant_phone: formData.tenant_phone,
      tenant_address: formData.tenant_address,
      move_in_date: formData.move_in_date,
      
      // Numeric fields - MUST convert to numbers for FastAPI validation
      agreement_duration: Number(formData.agreement_duration || 11),
      monthly_rent: Number(formData.monthly_rent || 0),
      
      // RENTAL: Has advance_deposit (security deposit)
      // LEASE: Has lease_amount (one-time payment), NO advance_deposit
      advance_deposit: (agreementType === 'rental') 
        ? Number(formData.advance_deposit || 0) 
        : 0,
      lease_amount: (agreementType === 'lease') 
        ? Number(formData.lease_amount || 0) 
        : null,
      
      payment_date: Number(formData.payment_date || 5),
      escalation_percent: Number(formData.escalation_percent || 5),
      notice_period: Number(formData.notice_period || 2),
      
      // Boolean clauses (already boolean from state)
      clause_subletting: formData.clause_subletting,
      clause_maintenance: formData.clause_maintenance,
      clause_structural: formData.clause_structural,
      clause_illegal: formData.clause_illegal,
      clause_late_penalty: formData.clause_late_penalty,
      clause_late_penalty_amount: Number(formData.clause_late_penalty_amount || 100),
      clause_inspection: formData.clause_inspection,
      clause_pets: formData.clause_pets,
      clause_pets_allowed: formData.clause_pets_allowed,
      clause_parking: formData.clause_parking,
      clause_parking_slot: formData.clause_parking_slot || null,
      clause_painting: formData.clause_painting,
      clause_society_rules: formData.clause_society_rules,
      clause_insurance: formData.clause_insurance,
      clause_arbitration: formData.clause_arbitration,
      
      // Property details
      furnishing: formData.furnishing,
      bedrooms: formData.bedrooms === '' ? 1 : Number(formData.bedrooms),
      bathrooms: formData.bathrooms === '' ? 1 : Number(formData.bathrooms),
      has_hall: formData.has_hall,
      has_kitchen: formData.has_kitchen,
      ac_count: Number(formData.ac_count || 0),
      geyser_count: Number(formData.geyser_count || 0),
      fridge_count: Number(formData.fridge_count || 0),
      washing_machine_count: Number(formData.washing_machine_count || 0),
      microwave_count: Number(formData.microwave_count || 0),
      tv_count: Number(formData.tv_count || 0),
      sofa_count: Number(formData.sofa_count || 0),
      bed_count: Number(formData.bed_count || 0),
      dining_table_count: Number(formData.dining_table_count || 0),
      wardrobe_count: Number(formData.wardrobe_count || 0),
      fan_count: Number(formData.fan_count || 2),
      light_count: Number(formData.light_count || 4),
      additional_notes: formData.additional_notes || null,
      
      // Payment
      stamp_value: Number(formData.stamp_value || 100),
    };
  };

  // Payment Integration
  const handlePayment = async () => {
    setIsProcessing(true);
    setPaymentError(null);

    try {
      // Step 1: Create order on backend
      const token = localStorage.getItem('hashvi_token');
      const API_URL = process.env.REACT_APP_API_URL || 'https://hashvi-rental-production.up.railway.app';

      const orderResponse = await fetch(`${API_URL}/agreements/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(prepareAgreementData())
      });

      if (!orderResponse.ok) {
        if (orderResponse.status === 401) {
          // Token expired - redirect to login
          alert('Your session has expired. Please login again.');
          localStorage.removeItem('hashvi_token');  // Fixed: Use 'hashvi_token'
          window.location.href = '/';
          return;
        }
        
        const errorData = await orderResponse.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create order');
      }

      const orderData = await orderResponse.json();
      // Store order ID in orderData - we'll use it directly in Razorpay options

      // Step 2: Open Razorpay Checkout
      const options = {
        key: orderData.key_id,
        // amount: orderData.amount * 100,  // REMOVED - Razorpay gets amount from order_id
        currency: 'INR',
        name: 'Hashvi Rental',
        description: `${agreementType === 'rental' ? 'Rental' : 'Lease'} Agreement - ${selectedUnit.unit_number}`,
        order_id: orderData.razorpay_order_id,
        handler: async function (response) {
          // Wait for Razorpay modal to fully close
          await new Promise(resolve => setTimeout(resolve, 1000));
          
          // Step 3: Verify payment
          try {
            const verifyResponse = await fetch(`${API_URL}/agreements/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify({
                order_id: String(orderData.order_id),
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            });

            if (!verifyResponse.ok) {
              throw new Error('Payment verification failed');
            }

            const verifyData = await verifyResponse.json();
            
            alert(`Payment Successful!\\n\\nAgreement ID: ${verifyData.agreement_id}\\n\\nDownload your PDF now.`);
            // Backend returns absolute URL, use it directly
            window.open(verifyData.pdf_url, '_blank');
            resetForm();
            
          } catch (error) {
            setPaymentError('Payment verification failed. Please contact support.');
            setIsProcessing(false);
          }
        },
        prefill: {
          name: formData.tenant_name,
          contact: formData.tenant_phone
        },
        theme: {
          color: '#f26522'
        },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            alert('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (error) {
      setPaymentError(error.message || 'Failed to initiate payment');
      setIsProcessing(false);
    }
  };
  
  // Step validation
  const canProceedStep1 = () => {
    const baseValid = formData.tenant_name && 
           formData.tenant_id_type &&
           formData.tenant_id_number &&
           formData.tenant_phone && 
           formData.tenant_address &&
           formData.move_in_date && 
           formData.monthly_rent;
    
    // For rental: require advance_deposit (security deposit)
    if (agreementType === 'rental') {
      return baseValid && formData.advance_deposit;
    }
    
    // For lease: require lease_amount (one-time payment)
    if (agreementType === 'lease') {
      return baseValid && formData.lease_amount;
    }
    
    return baseValid;
  };
  
  const canProceedStep3 = () => {
    return formData.bedrooms > 0 && formData.bathrooms > 0;
  };

  // Reset form
  const resetForm = () => {
    setView('list');
    setAgreementType(null);
    setCurrentStep(1);
    setSelectedUnit(null);
    setIsProcessing(false);  // Reset payment processing state
    setPaymentError(null);   // Clear any payment errors
    setFormData({
      tenant_name: '',
      tenant_id_type: 'aadhaar',
      tenant_id_number: '',
      tenant_phone: '',
      tenant_address: '',
      move_in_date: '',
      agreement_duration: 11,
      monthly_rent: '',
      advance_deposit: '',
      lease_amount: '', // NEW: Reset lease amount
      payment_date: 5,
      escalation_percent: 5,
      notice_period: 2,
      clause_subletting: true,
      clause_maintenance: true,
      clause_structural: true,
      clause_illegal: true,
      clause_late_penalty: false,
      clause_late_penalty_amount: 100,
      clause_inspection: true,
      clause_pets: false,
      clause_pets_allowed: false,
      clause_parking: false,
      clause_parking_slot: '',
      clause_painting: true,
      clause_society_rules: true,
      clause_insurance: false,
      clause_arbitration: true,
      furnishing: 'unfurnished',
      bedrooms: 1,
      bathrooms: 1,
      has_hall: true,
      has_kitchen: true,
      ac_count: 0,
      geyser_count: 0,
      fridge_count: 0,
      washing_machine_count: 0,
      microwave_count: 0,
      tv_count: 0,
      sofa_count: 0,
      bed_count: 0,
      dining_table_count: 0,
      wardrobe_count: 0,
      fan_count: 2,
      light_count: 4,
      additional_notes: '',
      stamp_value: 100,
    });
  };

  // Start new agreement
  const startNewAgreement = (type) => {
    setAgreementType(type);
    setView('create');
    setCurrentStep(1);
    // Set default duration based on agreement type
    setFormData(prev => ({
      ...prev,
      agreement_duration: type === 'lease' ? 12 : 11
    }));
  };

  // Select unit and auto-populate
  const selectUnit = (unit) => {
    setSelectedUnit(unit);
    // Auto-populate if tenant exists
    if (unit.tenant) {
      setFormData(prev => ({
        ...prev,
        tenant_name: unit.tenant.name || '',
        tenant_phone: unit.tenant.phone || '',
        tenant_address: unit.tenant.address || '', // Changed from email
        tenant_id_number: unit.tenant.aadhaar_last4 || '',
        monthly_rent: unit.rent_amount || '',
        advance_deposit: unit.tenant.deposit_amount || '',
        move_in_date: unit.tenant.move_in_date || '',
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        monthly_rent: unit.rent_amount || '',
      }));
    }
    setCurrentStep(1);
  };

  return (
    <div style={{ padding: 24 }}>
      {view === 'list' && <AgreementList onStartNew={startNewAgreement} />}
      
      {view === 'create' && (
        <CreateAgreementWizard 
          agreementType={agreementType}
          currentStep={currentStep}
          setCurrentStep={setCurrentStep}
          selectedUnit={selectedUnit}
          selectUnit={selectUnit}
          formData={formData}
          setFormData={setFormData}
          buildings={buildings}
          units={units}
          user={user}
          canProceedStep1={canProceedStep1}
          canProceedStep3={canProceedStep3}
          getAgreementEndDate={getAgreementEndDate}
          getTotalCost={getTotalCost}
          isProcessing={isProcessing}
          paymentError={paymentError}
          handlePayment={handlePayment}
          onCancel={resetForm}
        />
      )}
    </div>
  );
}

// Agreement List View
function AgreementList({ onStartNew }) {
  return (
    <div style={{ maxWidth: 800, margin: '0 auto' }}>
      <h2 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8, color: '#1e293b' }}>
        Agreement Generator
      </h2>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 32 }}>
        Create professional rental or lease agreements with legal compliance
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Rental Agreement Card */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 24,
          border: '1px solid #e2e8f0',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseOver={e => e.currentTarget.style.borderColor = '#f26522'}
        onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        onClick={() => onStartNew('rental')}>
          <div style={{ 
            width: 48, height: 48, borderRadius: 10, 
            background: 'linear-gradient(135deg, #f26522, #e05a1a)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16
          }}>
            <span style={{ fontSize: 24 }}>📄</span>
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
            Rental Agreement
          </h3>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
            11-month agreement. No registration required. Ideal for short-term rentals.
          </p>
          <div style={{ fontSize: 12, color: '#f26522', fontWeight: 600 }}>
            Starting from ₹299 →
          </div>
        </div>

        {/* Lease Agreement Card */}
        <div style={{
          background: 'white',
          borderRadius: 12,
          padding: 24,
          border: '1px solid #e2e8f0',
          cursor: 'pointer',
          transition: 'all 0.2s'
        }}
        onMouseOver={e => e.currentTarget.style.borderColor = '#1f4e79'}
        onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}
        onClick={() => onStartNew('lease')}>
          <div style={{ 
            width: 48, height: 48, borderRadius: 10, 
            background: 'linear-gradient(135deg, #1f4e79, #163a5f)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16
          }}>
            <span style={{ fontSize: 24 }}>📋</span>
          </div>
          <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
            Lease Agreement
          </h3>
          <p style={{ fontSize: 13, color: '#64748b', marginBottom: 16, lineHeight: 1.5 }}>
            12+ months. Registration required. For long-term leases.
          </p>
          <div style={{ fontSize: 12, color: '#1f4e79', fontWeight: 600 }}>
            Starting from ₹699 →
          </div>
        </div>
      </div>
    </div>
  );
}

// Create Agreement Wizard
function CreateAgreementWizard(props) {
  const {
    agreementType,
    currentStep,
    setCurrentStep,
    selectedUnit,
    selectUnit,
    formData,
    setFormData,
    buildings,
    units,
    user,
    canProceedStep1,
    canProceedStep3,
    getAgreementEndDate,
    getTotalCost,
    isProcessing,
    paymentError,
    handlePayment,
    onCancel
  } = props;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
          <button
            onClick={onCancel}
            style={{
              background: 'none',
              border: 'none',
              fontSize: 20,
              cursor: 'pointer',
              color: '#64748b',
              padding: 4
            }}>
            ←
          </button>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: '#1e293b', margin: 0 }}>
            {agreementType === 'rental' ? 'Rental Agreement' : 'Lease Agreement'}
          </h2>
        </div>
        <p style={{ fontSize: 14, color: '#64748b', marginLeft: 48 }}>
          {agreementType === 'rental' 
            ? '11-month rental agreement • No registration required'
            : '12+ month lease agreement • Registration required'
          }
        </p>
      </div>

      {/* Progress Steps */}
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        marginBottom: 32,
        padding: '0 24px'
      }}>
        {['Select Unit', 'Basic Details', 'Legal Clauses', 'Property Schedule', 'Preview', 'Stamp Selection', 'Payment'].map((label, idx) => {
          const stepNum = idx + 1;
          const isActive = currentStep === stepNum;
          const isCompleted = currentStep > stepNum;
          
          return (
            <React.Fragment key={stepNum}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  background: isCompleted ? '#10b981' : isActive ? '#f26522' : '#e2e8f0',
                  color: isCompleted || isActive ? 'white' : '#94a3b8',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 14,
                  fontWeight: 600,
                  marginBottom: 8
                }}>
                  {isCompleted ? '✓' : stepNum}
                </div>
                <div style={{
                  fontSize: 11,
                  color: isActive ? '#1e293b' : '#94a3b8',
                  fontWeight: isActive ? 600 : 400,
                  textAlign: 'center',
                  maxWidth: 80
                }}>
                  {label}
                </div>
              </div>
              {idx < 6 && (
                <div style={{
                  flex: 1,
                  height: 2,
                  background: isCompleted ? '#10b981' : '#e2e8f0',
                  marginBottom: 32
                }} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Step Content - Will add each step component */}
      <div style={{ 
        background: 'white', 
        borderRadius: 12, 
        padding: 32,
        border: '1px solid #e2e8f0' 
      }}>
        {currentStep === 1 && !selectedUnit && (
          <UnitSelectionStep 
            buildings={buildings}
            units={units}
            onSelectUnit={selectUnit}
          />
        )}
        
        {currentStep === 1 && selectedUnit && (
          <BasicDetailsStep 
            formData={formData}
            setFormData={setFormData}
            selectedUnit={selectedUnit}
            getAgreementEndDate={getAgreementEndDate}
            agreementType={agreementType}
            canProceed={canProceedStep1()}
            onNext={() => setCurrentStep(2)}
            onBack={() => selectUnit(null)}
          />
        )}
        
        {currentStep === 2 && (
          <LegalClausesStep 
            formData={formData}
            setFormData={setFormData}
            agreementType={agreementType}
            onNext={() => setCurrentStep(3)}
            onBack={() => setCurrentStep(1)}
          />
        )}
        
        {currentStep === 3 && (
          <PropertyScheduleStep 
            formData={formData}
            setFormData={setFormData}
            canProceed={canProceedStep3()}
            onNext={() => setCurrentStep(4)}
            onBack={() => setCurrentStep(2)}
          />
        )}
        
        {currentStep === 4 && (
          <PreviewStep 
            formData={formData}
            selectedUnit={selectedUnit}
            user={user}
            agreementType={agreementType}
            getAgreementEndDate={getAgreementEndDate}
            onNext={() => setCurrentStep(5)}
            onBack={() => setCurrentStep(3)}
          />
        )}
        
        {currentStep === 5 && (
          <StampSelectionStep 
            formData={formData}
            setFormData={setFormData}
            selectedUnit={selectedUnit}
            user={user}
            agreementType={agreementType}
            getAgreementEndDate={getAgreementEndDate}
            getTotalCost={getTotalCost}
            onNext={() => setCurrentStep(6)}
            onBack={() => setCurrentStep(4)}
          />
        )}
        
        {currentStep === 6 && (
          <StampPaperSelectionStep 
            formData={formData}
            setFormData={setFormData}
            agreementType={agreementType}
            getTotalCost={getTotalCost}
            onNext={() => setCurrentStep(7)}
            onBack={() => setCurrentStep(5)}
          />
        )}
        
        {currentStep === 7 && (
          <PaymentStep 
            formData={formData}
            selectedUnit={selectedUnit}
            user={user}
            getTotalCost={getTotalCost}
            isProcessing={isProcessing}
            paymentError={paymentError}
            onBack={() => setCurrentStep(6)}
            onConfirm={handlePayment}
          />
        )}
      </div>
    </div>
  );
}

// Unit Selection Step
function UnitSelectionStep({ buildings, units, onSelectUnit }) {
  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Select Unit
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Choose the property for which you want to generate the agreement
      </p>

      {buildings.map(building => {
        const buildingUnits = units.filter(u => u.building_id === building.id);
        
        return (
          <div key={building.id} style={{ marginBottom: 24 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 12 }}>
              {building.name}
            </h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
              {buildingUnits.map(unit => (
                <div
                  key={unit.id}
                  onClick={() => onSelectUnit(unit)}
                  style={{
                    padding: 16,
                    border: '2px solid #e2e8f0',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseOver={e => e.currentTarget.style.borderColor = '#f26522'}
                  onMouseOut={e => e.currentTarget.style.borderColor = '#e2e8f0'}>
                  <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4, color: '#1e293b' }}>
                    {unit.unit_number}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginBottom: 8 }}>
                    ₹{unit.rent_amount?.toLocaleString()}/month
                  </div>
                  <div style={{ 
                    fontSize: 11, 
                    padding: '2px 8px', 
                    borderRadius: 4,
                    background: unit.tenant ? '#dcfce7' : '#fef3c7',
                    color: unit.tenant ? '#166534' : '#92400e',
                    display: 'inline-block'
                  }}>
                    {unit.tenant ? unit.tenant.name : 'Vacant'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// I'll continue with the remaining step components in the next message due to length...
// This file is getting large. Let me know if you want me to split into multiple files or continue.

// CONTINUATION OF AgreementGenerator.js
// These components should be added to the main file

// Basic Details Step
export function BasicDetailsStep({ 
  formData, 
  setFormData, 
  selectedUnit,
  getAgreementEndDate,
  agreementType,
  canProceed,
  onNext,
  onBack
}) {
  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Basic Agreement Details
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Unit: {selectedUnit.unit_number} • {selectedUnit.building?.name}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {/* Tenant Name */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Tenant Name *
          </label>
          <input
            type="text"
            value={formData.tenant_name}
            onChange={e => {
              const value = e.target.value;
              // Only allow letters, spaces, and dots (for initials)
              if (/^[a-zA-Z\s.]*$/.test(value)) {
                setFormData({ ...formData, tenant_name: value });
              }
            }}
            placeholder="Full legal name"
            pattern="[a-zA-Z\s.]+"
            title="Only letters and spaces allowed"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}
          />
        </div>

        {/* Government ID Type */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Government ID Type *
          </label>
          <select
            value={formData.tenant_id_type}
            onChange={e => setFormData({ ...formData, tenant_id_type: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}>
            <option value="aadhaar">Aadhaar Card</option>
            <option value="pan">PAN Card</option>
            <option value="passport">Passport</option>
            <option value="driving_license">Driving License</option>
            <option value="voter_id">Voter ID</option>
          </select>
        </div>

        {/* Government ID Number */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            {formData.tenant_id_type === 'aadhaar' ? 'Aadhaar Number' : 
             formData.tenant_id_type === 'pan' ? 'PAN Number' :
             formData.tenant_id_type === 'passport' ? 'Passport Number' :
             formData.tenant_id_type === 'driving_license' ? 'DL Number' :
             'Voter ID Number'} *
          </label>
          <input
            type="text"
            value={formData.tenant_id_number}
            onChange={e => {
              const value = e.target.value.toUpperCase();
              // Validate based on ID type
              let isValid = true;
              
              if (formData.tenant_id_type === 'aadhaar') {
                // Only digits, max 12
                isValid = /^\d{0,12}$/.test(value.replace(/\s/g, ''));
              } else if (formData.tenant_id_type === 'pan') {
                // PAN format: 5 letters + 4 digits + 1 letter, max 10
                isValid = /^[A-Z]{0,5}\d{0,4}[A-Z]?$/.test(value);
              } else if (formData.tenant_id_type === 'passport') {
                // Alphanumeric, max 8
                isValid = /^[A-Z0-9]{0,8}$/.test(value);
              } else if (formData.tenant_id_type === 'driving_license') {
                // State code + digits, alphanumeric
                isValid = /^[A-Z0-9\s]{0,20}$/.test(value);
              } else {
                // Voter ID: alphanumeric, max 10
                isValid = /^[A-Z0-9]{0,10}$/.test(value);
              }
              
              if (isValid) {
                setFormData({ ...formData, tenant_id_number: value });
              }
            }}
            placeholder={
              formData.tenant_id_type === 'aadhaar' ? 'XXXX-XXXX-1234' :
              formData.tenant_id_type === 'pan' ? 'ABCDE1234F' :
              formData.tenant_id_type === 'passport' ? 'A1234567' :
              formData.tenant_id_type === 'driving_license' ? 'KA01 20230012345' :
              'ABC1234567'
            }
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}
          />
        </div>

        {/* Phone */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Phone Number *
          </label>
          <input
            type="tel"
            value={formData.tenant_phone}
            onChange={e => {
              const value = e.target.value;
              // Only digits, max 10
              if (/^\d{0,10}$/.test(value)) {
                setFormData({ ...formData, tenant_phone: value });
              }
            }}
            placeholder="10-digit mobile"
            maxLength="10"
            pattern="\d{10}"
            title="Must be exactly 10 digits"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}
          />
        </div>

        {/* Tenant Address */}
        <div style={{ gridColumn: '1 / -1' }}>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Tenant Address (as per ID) *
          </label>
          <textarea
            value={formData.tenant_address}
            onChange={e => setFormData({ ...formData, tenant_address: e.target.value })}
            placeholder="Complete address as per government ID"
            rows="2"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Move-in Date */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Move-in Date *
          </label>
          <input
            type="date"
            value={formData.move_in_date}
            onChange={e => setFormData({ ...formData, move_in_date: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}
          />
        </div>

        {/* Agreement Duration */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Agreement Duration (months) *
          </label>
          <input
            type={agreementType === 'lease' ? 'number' : 'text'}
            inputMode="numeric"
            value={formData.agreement_duration}
            onChange={e => {
              const value = e.target.value;
              
              if (agreementType === 'rental') {
                // RENTAL: 1-11 months, type="text"
                // Allow empty for editing
                if (value === '') {
                  setFormData({ ...formData, agreement_duration: '' });
                  return;
                }
                // Only allow 1-2 digits
                if (/^\d{1,2}$/.test(value)) {
                  const num = parseInt(value);
                  // Enforce max 11
                  if (num >= 1 && num <= 11) {
                    setFormData({ ...formData, agreement_duration: num });
                  }
                }
              } else {
                // LEASE: 12-60 months, type="number"
                // Allow empty for editing
                if (value === '') {
                  setFormData({ ...formData, agreement_duration: '' });
                  return;
                }
                const num = parseInt(value);
                // Enforce min 12, max 60
                if (!isNaN(num) && num >= 12 && num <= 60) {
                  setFormData({ ...formData, agreement_duration: num });
                } else if (!isNaN(num) && num < 12) {
                  // If less than 12, set to 12
                  setFormData({ ...formData, agreement_duration: 12 });
                } else if (!isNaN(num) && num > 60) {
                  // If more than 60, set to 60
                  setFormData({ ...formData, agreement_duration: 60 });
                }
              }
            }}
            onBlur={e => {
              // On blur, restore default if empty
              if (e.target.value === '' || e.target.value === '0') {
                setFormData({ 
                  ...formData, 
                  agreement_duration: agreementType === 'lease' ? 12 : 11 
                });
              }
            }}
            min={agreementType === 'rental' ? 1 : 12}
            max={agreementType === 'rental' ? 11 : 60}
            placeholder={agreementType === 'rental' ? '11' : '12'}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}
          />
          {formData.move_in_date && formData.agreement_duration && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              Ends on: {getAgreementEndDate()}
            </div>
          )}
        </div>

        {/* Lease Amount - ONLY for lease agreements */}
        {agreementType === 'lease' && (
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
              One-time Lease Amount *
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.lease_amount}
              onChange={e => {
                const value = e.target.value;
                // Only allow digits
                if (/^\d*$/.test(value)) {
                  setFormData({ ...formData, lease_amount: value });
                }
              }}
              placeholder="Amount in ₹ (paid upfront)"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              💡 This is the one-time lease deposit paid upfront (refundable at move-out)
            </div>
          </div>
        )}

        {/* Monthly Rent / Maintenance */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            {agreementType === 'lease' ? 'Monthly Maintenance *' : 'Monthly Rent *'}
          </label>
          <input
            type="text"
            inputMode="numeric"
            value={formData.monthly_rent}
            onChange={e => {
              const value = e.target.value;
              // Only allow digits
              if (/^\d*$/.test(value)) {
                setFormData({ ...formData, monthly_rent: value });
              }
            }}
            placeholder="Amount in ₹"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}
          />
          {agreementType === 'lease' && (
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>
              💡 Monthly maintenance paid to society
            </div>
          )}
        </div>

        {/* Security Deposit - ONLY for Rental agreements */}
        {agreementType === 'rental' && (
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
              Security Deposit *
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.advance_deposit}
              onChange={e => {
                const value = e.target.value;
                // Only allow digits
                if (/^\d*$/.test(value)) {
                  setFormData({ ...formData, advance_deposit: value });
                }
              }}
              placeholder="Amount in ₹"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
          </div>
        )}

        {/* Payment Date */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Monthly Payment Date
          </label>
          <select
            value={formData.payment_date}
            onChange={e => setFormData({ ...formData, payment_date: parseInt(e.target.value) })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}>
            <option value={5}>5th of every month</option>
            <option value={10}>10th of every month</option>
            <option value={15}>15th of every month</option>
            <option value={1}>1st of every month</option>
          </select>
        </div>

        {/* Escalation % - ONLY for rental agreements */}
        {agreementType === 'rental' && (
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
              Annual Rent Escalation (%)
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.escalation_percent}
              onChange={e => {
                const value = e.target.value;
                // Only allow digits, max 2 digits (0-99)
                if (/^\d{0,2}$/.test(value)) {
                  setFormData({ ...formData, escalation_percent: value });
                }
              }}
              placeholder="5"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
          </div>
        )}

        {/* Notice Period */}
        <div>
          <label style={{ display: 'block', fontSize: 13, fontWeight: 500, marginBottom: 6, color: '#475569' }}>
            Notice Period (months)
          </label>
          <select
            value={formData.notice_period}
            onChange={e => setFormData({ ...formData, notice_period: parseInt(e.target.value) })}
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: 8,
              border: '1px solid #e2e8f0',
              fontSize: 14
            }}>
            <option value={1}>1 month</option>
            <option value={2}>2 months</option>
            <option value={3}>3 months</option>
          </select>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Change Unit
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          style={{
            flex: 2,
            padding: 12,
            background: canProceed ? '#f26522' : '#cbd5e1',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: canProceed ? 'pointer' : 'not-allowed'
          }}>
          Continue to Legal Clauses →
        </button>
      </div>
    </div>
  );
}

// Legal Clauses Step
export function LegalClausesStep({ formData, setFormData, agreementType, onNext, onBack }) {
  const allClauses = [
    { key: 'subletting', label: 'Subletting Prohibition', desc: 'Tenant cannot sublet or transfer the property', default: true },
    { key: 'maintenance', label: 'Maintenance Responsibilities', desc: 'Defines tenant and landlord maintenance duties', default: true },
    { key: 'structural', label: 'Structural Modifications', desc: 'Prohibits structural changes without permission', default: true },
    { key: 'illegal', label: 'Illegal Activities', desc: 'Prohibits illegal business or unlawful activities', default: true },
    { key: 'late_penalty', label: 'Late Payment Penalty', desc: 'Penalty for delayed rent payment', default: false, hasAmount: true },
    { key: 'inspection', label: 'Inspection Rights', desc: 'Landlord can inspect property with notice', default: true },
    { key: 'pets', label: 'Pet Policy', desc: 'Define whether pets are allowed', default: false, hasOption: true },
    { key: 'parking', label: 'Parking Allocation', desc: 'Assign specific parking slot', default: false, hasSlot: true },
    { key: 'painting', label: 'Painting/Repair at Exit', desc: 'Tenant must paint or pay for painting when vacating', default: true },
    { key: 'society_rules', label: 'Society Rules Compliance', desc: 'Tenant must follow all society/building rules', default: true },
    { key: 'insurance', label: 'Insurance Requirement', desc: 'Require tenant to maintain insurance', default: false },
    { key: 'arbitration', label: 'Arbitration Clause', desc: 'Disputes resolved through arbitration', default: true },
  ];

  // Filter out late_penalty for lease agreements
  const clauses = agreementType === 'lease' 
    ? allClauses.filter(clause => clause.key !== 'late_penalty')
    : allClauses;

  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Legal Clauses
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Select which clauses to include in the agreement. Enabled clauses will appear in the final document.
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {clauses.map(clause => {
          const isEnabled = formData[`clause_${clause.key}`];
          
          return (
            <div key={clause.key} style={{
              padding: 16,
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              background: isEnabled ? '#f0f9ff' : 'white'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <input
                  type="checkbox"
                  checked={isEnabled}
                  onChange={e => setFormData({ ...formData, [`clause_${clause.key}`]: e.target.checked })}
                  style={{ marginTop: 4, width: 16, height: 16, cursor: 'pointer' }}
                />
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                    {clause.label}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>
                    {clause.desc}
                  </div>
                  
                  {/* Late Penalty Amount */}
                  {clause.hasAmount && isEnabled && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                        Penalty Amount (₹/day)
                      </label>
                      <input
                        type="text"
                        inputMode="numeric"
                        value={formData.clause_late_penalty_amount}
                        onChange={e => {
                          const value = e.target.value;
                          // Only allow digits
                          if (/^\d*$/.test(value)) {
                            setFormData({ ...formData, clause_late_penalty_amount: value });
                          }
                        }}
                        placeholder="100"
                        style={{
                          width: 150,
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid #e2e8f0',
                          fontSize: 14
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Pet Policy Option */}
                  {clause.hasOption && isEnabled && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                        Pets Policy
                      </label>
                      <select
                        value={formData.clause_pets_allowed}
                        onChange={e => setFormData({ ...formData, clause_pets_allowed: e.target.value === 'true' })}
                        style={{
                          width: 200,
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid #e2e8f0',
                          fontSize: 14
                        }}>
                        <option value="false">Not Allowed</option>
                        <option value="true">Allowed</option>
                      </select>
                    </div>
                  )}
                  
                  {/* Parking Slot */}
                  {clause.hasSlot && isEnabled && (
                    <div style={{ marginTop: 12 }}>
                      <label style={{ fontSize: 13, fontWeight: 500, marginBottom: 6, display: 'block' }}>
                        Parking Slot Number
                      </label>
                      <input
                        type="text"
                        value={formData.clause_parking_slot}
                        onChange={e => setFormData({ ...formData, clause_parking_slot: e.target.value })}
                        placeholder="e.g., P-12"
                        style={{
                          width: 150,
                          padding: '8px 12px',
                          borderRadius: 6,
                          border: '1px solid #e2e8f0',
                          fontSize: 14
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Back
        </button>
        <button
          onClick={onNext}
          style={{
            flex: 2,
            padding: 12,
            background: '#f26522',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}>
          Continue to Property Schedule →
        </button>
      </div>
    </div>
  );
}

// Continue in next file for PropertyScheduleStep and PreviewPaymentStep...
// FINAL STEPS for AgreementGenerator.js

// Property Schedule Step
export function PropertyScheduleStep({ formData, setFormData, canProceed, onNext, onBack }) {
  const appliances = [
    { key: 'ac_count', label: 'Air Conditioners', icon: '❄️' },
    { key: 'geyser_count', label: 'Geysers', icon: '🚿' },
    { key: 'fridge_count', label: 'Refrigerator', icon: '🧊' },
    { key: 'washing_machine_count', label: 'Washing Machine', icon: '🧺' },
    { key: 'microwave_count', label: 'Microwave', icon: '📟' },
    { key: 'tv_count', label: 'Television', icon: '📺' },
    { key: 'sofa_count', label: 'Sofa Set', icon: '🛋️' },
    { key: 'bed_count', label: 'Beds', icon: '🛏️' },
    { key: 'dining_table_count', label: 'Dining Table', icon: '🪑' },
    { key: 'wardrobe_count', label: 'Wardrobes', icon: '🚪' },
  ];

  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Property Schedule
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Provide detailed inventory of the property. Items with count 0 will not appear in the agreement.
      </p>

      {/* Furnishing Type */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'block', color: '#1e293b' }}>
          Furnishing Type
        </label>
        <div style={{ display: 'flex', gap: 12 }}>
          {['unfurnished', 'semi', 'fully'].map(type => (
            <button
              key={type}
              onClick={() => setFormData({ ...formData, furnishing: type })}
              style={{
                flex: 1,
                padding: 12,
                border: `2px solid ${formData.furnishing === type ? '#f26522' : '#e2e8f0'}`,
                background: formData.furnishing === type ? '#fff7ed' : 'white',
                borderRadius: 8,
                fontSize: 14,
                fontWeight: 500,
                cursor: 'pointer',
                color: formData.furnishing === type ? '#f26522' : '#64748b'
              }}>
              {type === 'fully' ? 'Fully Furnished' : type === 'semi' ? 'Semi Furnished' : 'Unfurnished'}
            </button>
          ))}
        </div>
      </div>

      {/* Room Configuration */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'block', color: '#1e293b' }}>
          Room Configuration
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, marginBottom: 6, display: 'block', color: '#64748b' }}>Bedrooms</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.bedrooms === '' ? '' : formData.bedrooms}
              onChange={e => {
                const value = e.target.value;
                // Allow empty string or single digit (0-9)
                if (value === '' || /^[0-9]$/.test(value)) {
                  setFormData({ ...formData, bedrooms: value });
                }
              }}
              placeholder="1"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, marginBottom: 6, display: 'block', color: '#64748b' }}>Bathrooms</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.bathrooms === '' ? '' : formData.bathrooms}
              onChange={e => {
                const value = e.target.value;
                // Allow empty string or single digit (0-9)
                if (value === '' || /^[0-9]$/.test(value)) {
                  setFormData({ ...formData, bathrooms: value });
                }
              }}
              placeholder="1"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, marginBottom: 6, display: 'block', color: '#64748b' }}>Hall</label>
            <input
              type="checkbox"
              checked={formData.has_hall}
              onChange={e => setFormData({ ...formData, has_hall: e.target.checked })}
              style={{ marginTop: 8, width: 20, height: 20 }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, marginBottom: 6, display: 'block', color: '#64748b' }}>Kitchen</label>
            <input
              type="checkbox"
              checked={formData.has_kitchen}
              onChange={e => setFormData({ ...formData, has_kitchen: e.target.checked })}
              style={{ marginTop: 8, width: 20, height: 20 }}
            />
          </div>
        </div>
      </div>

      {/* Appliances & Furniture */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'block', color: '#1e293b' }}>
          Appliances & Furniture
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          {appliances.map(item => (
            <div key={item.key} style={{
              padding: 12,
              border: '1px solid #e2e8f0',
              borderRadius: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 12
            }}>
              <span style={{ fontSize: 24 }}>{item.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{item.label}</div>
              </div>
              <input
                type="text"
                inputMode="numeric"
                value={formData[item.key]}
                onChange={e => {
                  const value = e.target.value;
                  // Only allow digits, max 2 digits
                  if (/^\d{0,2}$/.test(value)) {
                    setFormData({ ...formData, [item.key]: value });
                  }
                }}
                placeholder="0"
                style={{
                  width: 60,
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: '1px solid #e2e8f0',
                  fontSize: 14,
                  textAlign: 'center'
                }}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Electrical Fittings */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'block', color: '#1e293b' }}>
          Electrical Fittings
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ fontSize: 12, marginBottom: 6, display: 'block', color: '#64748b' }}>Fans</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.fan_count}
              onChange={e => {
                const value = e.target.value;
                // Only allow digits, max 2 digits
                if (/^\d{0,2}$/.test(value)) {
                  setFormData({ ...formData, fan_count: value });
                }
              }}
              placeholder="2"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
          </div>
          <div>
            <label style={{ fontSize: 12, marginBottom: 6, display: 'block', color: '#64748b' }}>Lights</label>
            <input
              type="text"
              inputMode="numeric"
              value={formData.light_count}
              onChange={e => {
                const value = e.target.value;
                // Only allow digits, max 2 digits
                if (/^\d{0,2}$/.test(value)) {
                  setFormData({ ...formData, light_count: value });
                }
              }}
              placeholder="4"
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: 6,
                border: '1px solid #e2e8f0',
                fontSize: 14
              }}
            />
          </div>
        </div>
      </div>

      {/* Additional Notes */}
      <div style={{ marginBottom: 24 }}>
        <label style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, display: 'block', color: '#1e293b' }}>
          Additional Notes (Optional)
        </label>
        <textarea
          value={formData.additional_notes}
          onChange={e => setFormData({ ...formData, additional_notes: e.target.value })}
          placeholder="Any additional property details, conditions, or specifications..."
          rows="3"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            fontSize: 14,
            fontFamily: 'inherit',
            resize: 'vertical'
          }}
        />
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, marginTop: 32 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Back
        </button>
        <button
          onClick={onNext}
          disabled={!canProceed}
          style={{
            flex: 2,
            padding: 12,
            background: canProceed ? '#f26522' : '#cbd5e1',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: canProceed ? 'pointer' : 'not-allowed'
          }}>
          Preview & Confirm →
        </button>
      </div>
    </div>
  );
}

// Preview & Payment Step
// Step 4: Preview Only
export function PreviewStep({ 
  formData, 
  selectedUnit,
  user,
  agreementType,
  getAgreementEndDate,
  onNext,
  onBack
}) {
  const getIdTypeLabel = () => {
    switch(formData.tenant_id_type) {
      case 'aadhaar': return 'Aadhaar';
      case 'pan': return 'PAN Card';
      case 'passport': return 'Passport';
      case 'driving_license': return 'Driving License';
      case 'voter_id': return 'Voter ID';
      default: return 'ID';
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Agreement Summary
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Review all details before proceeding to stamp selection
      </p>

      {/* Agreement Details */}
      <div style={{ 
        background: '#f8fafc', 
        padding: 24, 
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        marginBottom: 24
      }}>
        
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <InfoRow label="Agreement Type" value={agreementType === 'rental' ? 'Rental (11 months)' : 'Lease (12+ months)'} />
          <InfoRow label="Property" value={selectedUnit.unit_number} />
          <InfoRow label="Tenant Name" value={formData.tenant_name} />
          <InfoRow label={getIdTypeLabel()} value={formData.tenant_id_number} />
          <InfoRow label="Tenant Phone" value={formData.tenant_phone} />
          <InfoRow label="Tenant Address" value={formData.tenant_address} />
          <InfoRow label="Duration" value={`${formData.agreement_duration} months`} />
          <InfoRow label="Start Date" value={formData.move_in_date} />
          <InfoRow label="End Date" value={getAgreementEndDate()} />
          
          {/* Conditional display for lease vs rental */}
          {agreementType === 'lease' && formData.lease_amount && (
            <InfoRow label="One-time Lease Amount" value={`₹${parseInt(formData.lease_amount).toLocaleString()}`} />
          )}
          <InfoRow 
            label={agreementType === 'lease' ? 'Monthly Maintenance' : 'Monthly Rent'} 
            value={`₹${parseInt(formData.monthly_rent).toLocaleString()}`} 
          />
          
          {/* Security Deposit - ONLY for Rental */}
          {agreementType === 'rental' && (
            <InfoRow 
              label="Security Deposit" 
              value={`₹${parseInt(formData.advance_deposit).toLocaleString()}`} 
            />
          )}
          
          <InfoRow label="Payment Date" value={`${formData.payment_date}th of each month`} />
          <InfoRow label="Notice Period" value={`${formData.notice_period} months`} />
          
          {/* Only show escalation for rental */}
          {agreementType === 'rental' && (
            <InfoRow label="Escalation" value={`${formData.escalation_percent}% annually`} />
          )}
          
          <InfoRow label="Furnishing" value={formData.furnishing.charAt(0).toUpperCase() + formData.furnishing.slice(1)} />
        </div>

        <div style={{ marginTop: 20, paddingTop: 20, borderTop: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
            Included Clauses ({Object.keys(formData).filter(k => k.startsWith('clause_') && formData[k] === true).length}/12)
          </div>
          <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
            {Object.keys(formData)
              .filter(k => k.startsWith('clause_') && formData[k] === true)
              .map(k => k.replace('clause_', '').replace(/_/g, ' '))
              .map(c => c.charAt(0).toUpperCase() + c.slice(1))
              .join(' • ')}
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Back
        </button>
        <button
          onClick={onNext}
          style={{
            flex: 2,
            padding: 12,
            background: '#f26522',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}>
          Preview Agreement →
        </button>
      </div>
    </div>
  );
}

// Step 5: Agreement Text Preview (What will be printed)
export function StampSelectionStep({ 
  formData, 
  setFormData,
  selectedUnit,
  user,
  agreementType,
  getAgreementEndDate,
  getTotalCost,
  onNext,
  onBack
}) {
  const getIdTypeLabel = () => {
    switch(formData.tenant_id_type) {
      case 'aadhaar': return 'Aadhaar Card';
      case 'pan': return 'PAN Card';
      case 'passport': return 'Passport';
      case 'driving_license': return 'Driving License';
      case 'voter_id': return 'Voter ID';
      default: return 'ID';
    }
  };

  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Agreement Preview
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        This is the exact text that will be printed on stamp paper
      </p>

      {/* Agreement Document Preview */}
      <div style={{ 
        background: 'white',
        border: '2px solid #e2e8f0',
        borderRadius: 8,
        padding: 32,
        marginBottom: 24,
        fontFamily: 'serif',
        fontSize: 13,
        lineHeight: 1.8,
        maxHeight: 600,
        overflowY: 'auto'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 24, borderBottom: '2px solid #1e293b', paddingBottom: 16 }}>
          <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0, textTransform: 'uppercase', letterSpacing: 1 }}>
            {agreementType === 'lease' ? 'RESIDENTIAL LEASE AGREEMENT' : 'RENTAL AGREEMENT'}
          </h2>
        </div>

        {/* Execution Date */}
        <p style={{ marginBottom: 16 }}>
          This agreement of {agreementType === 'lease' ? 'lease' : 'rent'} made and executed at Bangalore on this <strong>{new Date(formData.move_in_date).getDate()}</strong> day of <strong>{new Date(formData.move_in_date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</strong>, by and between:
        </p>

        {/* Landlord Details */}
        <p style={{ marginBottom: 16 }}>
          <strong>{user.name || 'LANDLORD NAME'}</strong>, residing at <strong>{selectedUnit.building?.address || 'PROPERTY ADDRESS'}</strong>, hereinafter called the <strong>{agreementType === 'lease' ? 'LESSOR' : 'OWNER'}</strong> of the ONE PART,
        </p>

        <p style={{ marginBottom: 24, textAlign: 'center', fontWeight: 600 }}>AND</p>

        {/* Tenant Details */}
        <p style={{ marginBottom: 16 }}>
          <strong>{formData.tenant_name}</strong> ({getIdTypeLabel()} No. <strong>{formData.tenant_id_number}</strong>), residing at <strong>{formData.tenant_address}</strong>, Phone: <strong>{formData.tenant_phone}</strong>, hereinafter called the <strong>{agreementType === 'lease' ? 'LESSEE' : 'TENANT'}</strong> of the OTHER PART.
        </p>

        <p style={{ marginBottom: 24 }}>
          Whereas the {agreementType === 'lease' ? 'lessor' : 'owner'} is the absolute owner of the property described in the schedule hereunder and whereas the {agreementType === 'lease' ? 'lessee' : 'tenant'} has approached the {agreementType === 'lease' ? 'lessor' : 'owner'} to let out the schedule portion to the {agreementType === 'lease' ? 'lessee' : 'tenant'} and whereas the {agreementType === 'lease' ? 'lessor' : 'owner'} has agreed to let out the same to the {agreementType === 'lease' ? 'lessee' : 'tenant'} above named subject to the below mentioned terms and conditions.
        </p>

        {/* Terms & Conditions */}
        <h4 style={{ fontSize: 15, fontWeight: 700, marginTop: 24, marginBottom: 16, textTransform: 'uppercase' }}>
          TERMS & CONDITIONS:
        </h4>

        <ol style={{ paddingLeft: 20 }}>
          <li style={{ marginBottom: 12 }}>
            The duration of this {agreementType === 'lease' ? 'lease' : 'rental'} agreement is for <strong>{formData.agreement_duration} months</strong>, which shall be commencing from <strong>{formData.move_in_date}</strong> to <strong>{getAgreementEndDate()}</strong>{agreementType === 'rental' ? ' and it may be renewed for further period on mutual consent of both the parties' : ', unless cancelled earlier under any provision of this Agreement'}.
          </li>

          {agreementType === 'lease' ? (
            <>
              <li style={{ marginBottom: 12 }}>
                The lessee has paid to the lessor a sum of <strong>Rs. {parseInt(formData.lease_amount || 0).toLocaleString()}/-</strong> (Rupees {numberToWords(formData.lease_amount || 0)} only) as lease deposit, which is free of interest, which the lessor does accept and acknowledge. This deposit is for the due performance and observance of the terms and conditions of this Agreement. The lease deposit shall be returned to the lessee simultaneously with the lessee's vacating the said premises.
              </li>

              <li style={{ marginBottom: 12 }}>
                In consideration of use of the said premises, the lessee agrees to pay monthly flat maintenance charges of <strong>Rs. {parseInt(formData.monthly_rent).toLocaleString()}/-</strong> (Rupees {numberToWords(formData.monthly_rent)} only) to the apartment association directly on or before <strong>{formData.payment_date}th day</strong> of every calendar month.
              </li>
            </>
          ) : (
            <>
              <li style={{ marginBottom: 12 }}>
                The tenant has agreed to pay a monthly rent of <strong>Rs. {parseInt(formData.monthly_rent).toLocaleString()}/-</strong> (Rupees {numberToWords(formData.monthly_rent)} only) and the tenant has agreed to pay the rent on <strong>{formData.payment_date}th day</strong> of every calendar month.
              </li>

              <li style={{ marginBottom: 12 }}>
                The tenant has paid a sum of <strong>Rs. {parseInt(formData.advance_deposit).toLocaleString()}/-</strong> (Rupees {numberToWords(formData.advance_deposit)} only) as an advance amount towards the scheduled premises and it shall not carry any interest.
              </li>

              <li style={{ marginBottom: 12 }}>
                The owner has agreed to refund the above said advance amount to the tenant while vacating the schedule premises by deducting arrears if any in respect of rent.
              </li>
            </>
          )}

          {agreementType === 'rental' && formData.escalation_percent > 0 && (
            <li style={{ marginBottom: 12 }}>
              After completion of every 11 months, an increase of <strong>{formData.escalation_percent}%</strong> of rent amount should be paid along with prevailing rent amount.
            </li>
          )}

          {formData.clause_subletting && (
            <li style={{ marginBottom: 12 }}>
              <strong>Subletting Prohibition:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} shall not sublet, assign, or transfer the premises to any third party without prior written consent of the {agreementType === 'lease' ? 'lessor' : 'owner'}. Any violation shall result in immediate termination of this agreement.
            </li>
          )}

          {formData.clause_maintenance && (
            <li style={{ marginBottom: 12 }}>
              <strong>Maintenance Responsibilities:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} shall be responsible for day-to-day maintenance including minor repairs. The {agreementType === 'lease' ? 'lessee' : 'tenant'} shall pay all utility charges including electricity, water, gas, and internet. The {agreementType === 'lease' ? 'lessor' : 'owner'} shall be responsible for major structural repairs and common area maintenance.
            </li>
          )}

          {formData.clause_structural && (
            <li style={{ marginBottom: 12 }}>
              <strong>Structural Modifications:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} shall not make any structural alterations, additions, or modifications to the premises without prior written permission from the {agreementType === 'lease' ? 'lessor' : 'owner'}. Any unauthorized changes shall be restored to original condition at {agreementType === 'lease' ? 'lessee' : 'tenant'}'s expense.
            </li>
          )}

          {formData.clause_illegal && (
            <li style={{ marginBottom: 12 }}>
              <strong>Illegal Activities:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} shall not carry on any illegal business, trade in contraband goods, engage in unlawful activities, or store any inflammable, hazardous, or dangerous materials in the premises.
            </li>
          )}

          {formData.clause_late_penalty && agreementType === 'rental' && (
            <li style={{ marginBottom: 12 }}>
              <strong>Late Payment Penalty:</strong> In case of delayed rent payment beyond the due date, the tenant shall pay a late payment penalty of <strong>₹{formData.clause_late_penalty_amount}</strong> per day until full payment is made.
            </li>
          )}

          {formData.clause_inspection && (
            <li style={{ marginBottom: 12 }}>
              <strong>Inspection Rights:</strong> The {agreementType === 'lease' ? 'lessor or lessor' : 'owner or owner'}'s authorized representative shall have the right to inspect the premises with reasonable prior notice to the {agreementType === 'lease' ? 'lessee' : 'tenant'}, typically 24-48 hours in advance, to ensure proper maintenance and compliance with terms.
            </li>
          )}

          {formData.clause_pets && (
            <li style={{ marginBottom: 12 }}>
              <strong>Pet Policy:</strong> Pets are {formData.clause_pets_allowed ? 'ALLOWED' : 'NOT ALLOWED'} in the premises. {!formData.clause_pets_allowed && 'Any violation of this policy may result in termination of the agreement.'}
            </li>
          )}

          {formData.clause_parking && (
            <li style={{ marginBottom: 12 }}>
              <strong>Parking Allocation:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} is allocated parking slot <strong>{formData.clause_parking_slot}</strong> for exclusive use during the tenancy period.
            </li>
          )}

          <li style={{ marginBottom: 12 }}>
            <strong>Notice Period:</strong> Both parties shall give <strong>{formData.notice_period} months</strong> written notice in advance for vacating the premises or termination of this agreement after completion of any lock-in period.
          </li>

          {formData.clause_painting && (
            <li style={{ marginBottom: 12 }}>
              <strong>Painting/Repair at Exit:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} hereby agrees to {agreementType === 'lease' ? `pay 2% of lease amount (₹${parseInt((formData.lease_amount || 0) * 0.02)}) towards painting charges` : 'either paint the scheduled property at their own expense or pay one month\'s rent as painting charges to the owner'} at the time of vacating the premises.
            </li>
          )}

          {formData.clause_society_rules && (
            <li style={{ marginBottom: 12 }}>
              <strong>Society Rules Compliance:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} shall strictly comply with all rules, regulations, and bylaws of the housing society/building/apartment complex and shall not cause any nuisance or disturbance to neighbors.
            </li>
          )}

          {formData.clause_insurance && (
            <li style={{ marginBottom: 12 }}>
              <strong>Insurance Requirement:</strong> The {agreementType === 'lease' ? 'lessee' : 'tenant'} is required to maintain appropriate insurance coverage for their personal belongings and shall provide proof of insurance to the {agreementType === 'lease' ? 'lessor' : 'owner'} upon request.
            </li>
          )}

          {formData.clause_arbitration && (
            <li style={{ marginBottom: 12 }}>
              <strong>Arbitration Clause:</strong> In case of any dispute arising out of this agreement, both parties agree to first attempt resolution through mediation. If mediation fails, disputes shall be resolved through arbitration in accordance with the Arbitration and Conciliation Act, 1996, and the jurisdiction shall be Bangalore, Karnataka.
            </li>
          )}
        </ol>

        {/* Schedule */}
        <h4 style={{ fontSize: 15, fontWeight: 700, marginTop: 24, marginBottom: 16, textTransform: 'uppercase' }}>
          SCHEDULE
        </h4>

        <p style={{ marginBottom: 16 }}>
          All that piece and parcel of residential premises situated at <strong>{selectedUnit.building?.address || 'PROPERTY ADDRESS'}</strong>, Unit <strong>{selectedUnit.unit_number}</strong>, <strong>{formData.furnishing}</strong> furnished accommodation consisting of:
        </p>

        <ul style={{ paddingLeft: 20, marginBottom: 24 }}>
          {formData.has_hall && <li>Hall</li>}
          {formData.bedrooms > 0 && <li>{formData.bedrooms} Bedroom(s)</li>}
          {formData.bathrooms > 0 && <li>{formData.bathrooms} Bathroom(s)</li>}
          {formData.has_kitchen && <li>Kitchen</li>}
          {formData.ac_count > 0 && <li>Air Conditioner ({formData.ac_count})</li>}
          {formData.geyser_count > 0 && <li>Geyser ({formData.geyser_count})</li>}
          {formData.fridge_count > 0 && <li>Refrigerator ({formData.fridge_count})</li>}
          {formData.washing_machine_count > 0 && <li>Washing Machine ({formData.washing_machine_count})</li>}
          {formData.tv_count > 0 && <li>Television ({formData.tv_count})</li>}
          {formData.sofa_count > 0 && <li>Sofa Set ({formData.sofa_count})</li>}
          {formData.bed_count > 0 && <li>Bed(s) ({formData.bed_count})</li>}
          {formData.wardrobe_count > 0 && <li>Wardrobe(s) ({formData.wardrobe_count})</li>}
          {formData.fan_count > 0 && <li>Ceiling Fans ({formData.fan_count})</li>}
          {formData.light_count > 0 && <li>Light Fittings ({formData.light_count})</li>}
          <li>Water and electric facilities</li>
        </ul>

        {formData.additional_notes && (
          <p style={{ marginBottom: 16, fontStyle: 'italic' }}>
            <strong>Additional Notes:</strong> {formData.additional_notes}
          </p>
        )}

        {/* Signatures */}
        <p style={{ marginTop: 32, marginBottom: 32, textAlign: 'center', fontWeight: 600 }}>
          IN WITNESS THEREOF both the parties have affixed their signature to this rental agreement.
        </p>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, marginTop: 40 }}>
          <div>
            <p style={{ marginBottom: 8 }}>WITNESSES:</p>
            <p style={{ marginBottom: 24 }}>1. _____________________</p>
            <p style={{ marginBottom: 40 }}>2. _____________________</p>
          </div>
          <div>
            <p style={{ marginBottom: 60 }}>{agreementType === 'lease' ? 'LESSOR' : 'OWNER'}</p>
            <p style={{ marginTop: 60 }}>{agreementType === 'lease' ? 'LESSEE' : 'TENANT'}</p>
          </div>
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Back to Summary
        </button>
        <button
          onClick={onNext}
          style={{
            flex: 2,
            padding: 12,
            background: '#f26522',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: 'pointer'
          }}>
          Looks Good - Select Stamp Paper →
        </button>
      </div>
    </div>
  );
}

// Helper function to convert numbers to words (simple implementation)
function numberToWords(num) {
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  
  if (num === 0) return 'Zero';
  if (num < 10) return ones[num];
  if (num < 20) return teens[num - 10];
  if (num < 100) return tens[Math.floor(num / 10)] + (num % 10 !== 0 ? ' ' + ones[num % 10] : '');
  if (num < 1000) return ones[Math.floor(num / 100)] + ' Hundred' + (num % 100 !== 0 ? ' ' + numberToWords(num % 100) : '');
  if (num < 100000) return numberToWords(Math.floor(num / 1000)) + ' Thousand' + (num % 1000 !== 0 ? ' ' + numberToWords(num % 1000) : '');
  if (num < 10000000) return numberToWords(Math.floor(num / 100000)) + ' Lakh' + (num % 100000 !== 0 ? ' ' + numberToWords(num % 100000) : '');
  return num.toString();
}

// Step 6: Stamp Paper Selection
export function StampPaperSelectionStep({ 
  formData, 
  setFormData,
  agreementType,
  getTotalCost,
  onNext,
  onBack
}) {
  const allStampOptions = [
    { value: 100, label: '₹100 Stamp Paper', desc: 'Recommended for rent < ₹20,000/month' },
    { value: 500, label: '₹500 Stamp Paper', desc: 'Recommended for rent > ₹20,000/month' },
  ];

  // For lease agreements, only show ₹500 stamp option
  const stampOptions = agreementType === 'lease' 
    ? allStampOptions.filter(opt => opt.value === 500)
    : allStampOptions;

  // Auto-select ₹500 for lease if not already selected
  React.useEffect(() => {
    if (agreementType === 'lease' && formData.stamp_value !== 500) {
      setFormData(prev => ({ ...prev, stamp_value: 500 }));
    }
  }, [agreementType, formData.stamp_value, setFormData]);

  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Select Stamp Paper Value
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        {agreementType === 'lease' 
          ? 'Lease agreements require ₹500 stamp paper'
          : 'Choose the appropriate stamp paper value for your agreement'
        }
      </p>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        {stampOptions.map(option => (
          <div
            key={option.value}
            onClick={() => setFormData({ ...formData, stamp_value: option.value })}
            style={{
              padding: 20,
              border: `2px solid ${formData.stamp_value === option.value ? '#f26522' : '#e2e8f0'}`,
              background: formData.stamp_value === option.value ? '#fff7ed' : 'white',
              borderRadius: 12,
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                {option.label}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>
                {option.desc}
              </div>
            </div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#f26522' }}>
              ₹{option.value + 90 + 60 + 49}
            </div>
          </div>
        ))}
      </div>

      {/* Cost Breakdown */}
      <div style={{ 
        background: '#f8fafc', 
        padding: 20, 
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        marginBottom: 24
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
          Cost Breakdown
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: '#64748b' }}>Stamp Paper</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>₹{formData.stamp_value}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: '#64748b' }}>Printing & Binding</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>₹90</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: '#64748b' }}>Courier Delivery</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>₹60</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
            <span style={{ color: '#64748b' }}>Service Fee</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>₹49</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            fontSize: 16,
            paddingTop: 12,
            marginTop: 8,
            borderTop: '1px solid #e2e8f0'
          }}>
            <span style={{ color: '#1e293b', fontWeight: 600 }}>Total</span>
            <span style={{ color: '#f26522', fontWeight: 700 }}>₹{getTotalCost()}</span>
          </div>
        </div>
      </div>

      <div style={{ 
        padding: 16,
        background: '#eff6ff',
        borderRadius: 8,
        border: '1px solid #bfdbfe',
        fontSize: 13,
        color: '#1e40af',
        lineHeight: 1.5,
        marginBottom: 24
      }}>
        ⏱️ <strong>Delivery Timeline:</strong> Agreement will be printed on stamp paper and delivered to your registered address within 2-3 business days.
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Back to Preview
        </button>
        <button
          onClick={onNext}
          disabled={!formData.stamp_value}
          style={{
            flex: 2,
            padding: 12,
            background: formData.stamp_value ? '#f26522' : '#cbd5e1',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 600,
            cursor: formData.stamp_value ? 'pointer' : 'not-allowed'
          }}>
          Continue to Payment →
        </button>
      </div>
    </div>
  );
}

// Step 7: Payment
export function PaymentStep({ 
  formData, 
  selectedUnit,
  user,
  getTotalCost,
  isProcessing,
  paymentError,
  onBack,
  onConfirm
}) {
  return (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: '#1e293b' }}>
        Payment
      </h3>
      <p style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
        Complete payment to generate your agreement
      </p>

      {/* Error Message */}
      {paymentError && (
        <div style={{
          padding: 16,
          background: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: 8,
          marginBottom: 24,
          color: '#991b1b'
        }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>❌ Payment Failed</div>
          <div style={{ fontSize: 13 }}>{paymentError}</div>
        </div>
      )}

      {/* Order Summary */}
      <div style={{ 
        background: '#f8fafc', 
        padding: 24, 
        borderRadius: 8,
        border: '1px solid #e2e8f0',
        marginBottom: 24
      }}>
        <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#1e293b' }}>
          Order Summary
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: '#64748b' }}>Property</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>{selectedUnit.unit_number}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: '#64748b' }}>Tenant</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>{formData.tenant_name}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14 }}>
            <span style={{ color: '#64748b' }}>Stamp Paper</span>
            <span style={{ color: '#1e293b', fontWeight: 500 }}>₹{formData.stamp_value}</span>
          </div>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            fontSize: 18,
            paddingTop: 12,
            marginTop: 8,
            borderTop: '1px solid #e2e8f0'
          }}>
            <span style={{ color: '#1e293b', fontWeight: 600 }}>Total Amount</span>
            <span style={{ color: '#f26522', fontWeight: 700 }}>₹{getTotalCost()}</span>
          </div>
        </div>
      </div>

      {/* Payment Info */}
      <div style={{ 
        padding: 20,
        background: 'white',
        borderRadius: 12,
        border: '2px solid #f26522',
        marginBottom: 24
      }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: '#1e293b' }}>
          💳 Secure Payment via Razorpay
        </div>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6, marginBottom: 16 }}>
          Your payment is secured by Razorpay. You can pay using Credit Card, Debit Card, UPI, Net Banking, or Wallets.
        </div>
        <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.6 }}>
          ✓ Secure 256-bit encryption<br/>
          ✓ PCI DSS compliant<br/>
          ✓ Instant confirmation
        </div>
      </div>

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12 }}>
        <button
          onClick={onBack}
          style={{
            flex: 1,
            padding: 12,
            background: '#f1f5f9',
            border: 'none',
            borderRadius: 8,
            fontSize: 14,
            fontWeight: 500,
            cursor: 'pointer'
          }}>
          ← Back
        </button>
        <button
          onClick={onConfirm}
          disabled={isProcessing}
          style={{
            flex: 2,
            padding: 14,
            background: isProcessing ? '#cbd5e1' : 'linear-gradient(135deg, #f26522, #e05a1a)',
            color: 'white',
            border: 'none',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 600,
            cursor: isProcessing ? 'not-allowed' : 'pointer',
            boxShadow: isProcessing ? 'none' : '0 4px 12px rgba(242,101,34,0.3)',
            opacity: isProcessing ? 0.7 : 1
          }}>
          {isProcessing ? '⏳ Processing Payment...' : `Pay ₹${getTotalCost()} via Razorpay →`}
        </button>
      </div>
    </div>
  );
}

// Helper component for info rows
function InfoRow({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: '#1e293b' }}>{value}</div>
    </div>
  );
}
